import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ThemeToggle, { ThemeMode } from './ToggleSwitch'
import { ROUTES } from '../routes'

const LeftMenu: React.FC = () => {
  const navigate = useNavigate();
  const [themeMode, setThemeMode] = useState<ThemeMode>('auto');

  // Load saved theme preference on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as ThemeMode;
    if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
      setThemeMode(savedTheme);
    }
  }, []);

  // Apply theme changes
  useEffect(() => {
    const root = document.documentElement;
    
    // Save theme preference
    localStorage.setItem('theme', themeMode);
    
    if (themeMode === 'auto') {
      // Auto mode: follow system preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const applyAutoTheme = () => {
        if (mediaQuery.matches) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      };
      
      // Apply immediately
      applyAutoTheme();
      
      // Listen for system theme changes
      mediaQuery.addEventListener('change', applyAutoTheme);
      
      return () => {
        mediaQuery.removeEventListener('change', applyAutoTheme);
      };
    } else if (themeMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [themeMode]);

  const handleThemeChange = (mode: ThemeMode) => {
    setThemeMode(mode);
  };
  return (
    <div className="sticky top-20 left-0 hidden h-full shrink-0 lg:block">
      {/* Menu */}
      <div className="w-full min-w-[144px] shrink-0 px-10 py-2">
        <nav className="w-full flex-1 shrink-0" role="menu">
          <ul className="w-full space-y-4">
            <li>
              <a
                href="#"
                className="menu-link group/link active"
                onClick={e => {
                  e.preventDefault();
                  navigate(ROUTES.HOME_PAGE);
                }}
              >
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="fill-grey-50"
                    d="M11.3387 1.24963C11.7161 0.91842 12.2807 0.918413 12.658 1.24963L19.1483 6.99963L19.9979 7.73987L21.658 9.24963C22.0717 9.61439 22.1119 10.2454 21.7479 10.6598C21.3831 11.0734 20.7531 11.1136 20.3387 10.7496L19.9979 10.4498V18.9996C19.9979 20.6565 18.6547 21.9996 16.9979 21.9996H13.9979C13.4458 21.9994 12.9979 21.5518 12.9979 20.9996V15.9996C13.002 15.7394 12.8976 15.4893 12.7088 15.3102C12.5234 15.1156 12.2666 15.0034 11.9979 14.9996C11.4459 14.9999 10.9981 15.4477 10.9979 15.9996V20.9996C10.9979 21.5519 10.5502 21.9996 9.99788 21.9996H6.99788C5.34125 21.9994 3.99788 20.6563 3.99788 18.9996V10.4498L3.65804 10.7496C3.24112 11.0854 2.63376 11.0333 2.28011 10.6315C1.92676 10.2296 1.95272 9.62037 2.3387 9.24963L3.99788 7.73987L4.84847 6.99963L11.3387 1.24963ZM5.99788 8.66956V18.9996C5.99788 19.5518 6.44582 19.9994 6.99788 19.9996H8.99788V15.9996C8.99808 14.3431 10.3414 12.9999 11.9979 12.9996C13.6546 12.9996 14.9977 14.3429 14.9979 15.9996V19.9996H16.9979C17.5502 19.9996 17.9979 19.5519 17.9979 18.9996V8.66956L11.9979 3.34045L5.99788 8.66956Z"
                  />
                </svg>
                Home
              </a>
            </li>
            <li>
              <a
                href="#"
                className="menu-link"
                onClick={e => {
                  e.preventDefault();
                  navigate(ROUTES.SESSIONS);
                }}
              >
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 2C7.55228 2 8 2.44772 8 3V4H16V3C16 2.44772 16.4477 2 17 2C17.5523 2 18 2.44772 18 3V4H19C20.6569 4 22 5.34315 22 7V19C22 20.6569 20.6569 22 19 22H5C3.34315 22 2 20.6569 2 19V7C2 5.34315 3.34315 4 5 4H6V3C6 2.44772 6.44772 2 7 2ZM19 10H5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V10ZM16 12C16.5523 12 17 12.4477 17 13V15C17 15.5523 16.5523 16 16 16H14C13.4477 16 13 15.5523 13 15V13C13 12.4477 13.4477 12 14 12H16ZM16 6H6C5.44772 6 5 6.44772 5 7V8H19V7C19 6.44772 18.5523 6 18 6H16Z"
                    fill="#808080"
                  />
                </svg>
                Sessions
              </a>
            </li>
          </ul>
        </nav>
        <hr className="border-grey-70 my-2" />

        {/* Theme Toggle Section */}
        <ThemeToggle
          mode={themeMode}
          onChange={handleThemeChange}
        />
        
        <div className="mt-6 flex justify-center">
          <button className="btn btn-primary" onClick={() => navigate('/create-session')}>
            <svg
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="fill-current"
                d="M12 6C12.5523 6 13 6.44772 13 7V11H17C17.5523 11 18 11.4477 18 12C18 12.5523 17.5523 13 17 13H13V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V13H7C6.44772 13 6 12.5523 6 12C6 11.4477 6.44772 11 7 11H11V7C11 6.44772 11.4477 6 12 6Z"
              />
            </svg>
            <span>New Session</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default LeftMenu


